import dayjs, { type Dayjs } from 'dayjs'
import { computed, ref, toValue, type ComputedRef, type MaybeRefOrGetter, type Ref } from 'vue'

// types
import type {
  CalendarDayCell,
  CalendarRange,
  CalendarTimeSlot,
  CalendarViewType,
  CalendarWeekStart
} from '@/types/components/calendar'

// utilities
import { toCalendarDate, type CalendarDateInput } from '@/utilities/calendar/datetime'
import { formatPeriodTitle, getWeekdayNames } from '@/utilities/calendar/format'
import { buildMonthGrid, buildTimeSlots, buildViewDays, getVisibleRange, shiftCursor } from '@/utilities/calendar/grid'

/**
 * Options accepted by useCalendar; all display settings are reactive-friendly.
 */
export interface UseCalendarOptions {
  /** View shown on first render */
  initialView?: CalendarViewType
  /** Date the calendar opens on, defaults to today */
  initialDate?: CalendarDateInput
  /** 0 = Sunday through 6 = Saturday */
  weekStart?: MaybeRefOrGetter<CalendarWeekStart>
  /** First hour shown in the time grid */
  dayStartHour?: MaybeRefOrGetter<number>
  /** Last hour shown in the time grid */
  dayEndHour?: MaybeRefOrGetter<number>
  /** Spacing between time-grid lines, in minutes */
  slotMinutes?: MaybeRefOrGetter<number>
  /** Use 12-hour clock labels */
  hour12?: MaybeRefOrGetter<boolean>
  /** Current moment, injected so the now-indicator and "today" styling stay testable */
  now?: MaybeRefOrGetter<Dayjs>
}

/**
 * Everything the Calendar component needs to render and navigate a period.
 */
export interface UseCalendarReturn {
  /** Active view */
  view: Ref<CalendarViewType>
  /** Date anchoring the visible period */
  cursor: Ref<Dayjs>
  /** Toolbar period title */
  title: ComputedRef<string>
  /** Inclusive start and exclusive end of the visible period */
  visibleRange: ComputedRef<CalendarRange>
  /** Every visible day, flattened across views */
  days: ComputedRef<CalendarDayCell[]>
  /** Month grid as rows of seven days; empty outside month view */
  weeks: ComputedRef<CalendarDayCell[][]>
  /** Weekday header names, rotated to the configured first day */
  weekdayNames: ComputedRef<string[]>
  /** Horizontal lines of the time-grid axis */
  timeSlots: ComputedRef<CalendarTimeSlot[]>
  /** First minute of the time grid, measured from midnight */
  gridStartMinute: ComputedRef<number>
  /** Last minute of the time grid, measured from midnight */
  gridEndMinute: ComputedRef<number>
  /** Whether the visible period contains today */
  isTodayVisible: ComputedRef<boolean>
  /** Move forward one period */
  next: () => void
  /** Move back one period */
  prev: () => void
  /** Jump to the period containing now */
  goToToday: () => void
  /** Jump to the period containing a date */
  goToDate: (date: CalendarDateInput) => void
  /** Switch view, keeping the cursor within the period on screen */
  setView: (next: CalendarViewType) => void
}

/**
 * Holds the calendar's navigation state and derives everything the views render from it.
 * Pure state only: no DOM access and no lifecycle hooks, so it can be unit tested directly.
 * @param options - Display and navigation options
 */
export function useCalendar(options: UseCalendarOptions = {}): UseCalendarReturn {
  const view = ref<CalendarViewType>(options.initialView ?? 'month')
  const cursor = ref<Dayjs>(toCalendarDate(options.initialDate) ?? dayjs())

  /** Current moment, from the caller when provided so tests can freeze it */
  const now = computed<Dayjs>(() => toValue(options.now) ?? dayjs())

  /** Configured first day of the week */
  const weekStart = computed<CalendarWeekStart>(() => toValue(options.weekStart) ?? 1)

  const gridStartMinute = computed(() => Math.max(0, Math.round((toValue(options.dayStartHour) ?? 0) * 60)))

  const gridEndMinute = computed(() => Math.min(1440, Math.round((toValue(options.dayEndHour) ?? 24) * 60)))

  const title = computed(() => formatPeriodTitle(cursor.value, view.value, weekStart.value))

  const visibleRange = computed(() => getVisibleRange(cursor.value, view.value, weekStart.value))

  const days = computed(() => buildViewDays(cursor.value, view.value, weekStart.value, now.value))

  const weeks = computed(() =>
    // Only month view has rows; other views render a flat column list
    view.value === 'month' ? buildMonthGrid(cursor.value, weekStart.value, now.value) : []
  )

  const weekdayNames = computed(() => getWeekdayNames(weekStart.value, cursor.value))

  const timeSlots = computed(() =>
    buildTimeSlots(
      gridStartMinute.value / 60,
      gridEndMinute.value / 60,
      toValue(options.slotMinutes) ?? 60,
      toValue(options.hour12) ?? false
    )
  )

  const isTodayVisible = computed(() => {
    const start = dayjs(visibleRange.value.start)
    const end = dayjs(visibleRange.value.end)

    return !now.value.isBefore(start) && now.value.isBefore(end)
  })

  /**
   * Moves forward one period in the active view.
   */
  function next(): void {
    cursor.value = shiftCursor(cursor.value, view.value, 1)
  }

  /**
   * Moves back one period in the active view.
   */
  function prev(): void {
    cursor.value = shiftCursor(cursor.value, view.value, -1)
  }

  /**
   * Jumps to the period containing the current moment.
   */
  function goToToday(): void {
    cursor.value = now.value.startOf('day')
  }

  /**
   * Jumps to the period containing a date, ignoring unparseable input.
   * @param date - Date to move to
   */
  function goToDate(date: CalendarDateInput): void {
    const parsed = toCalendarDate(date)

    // Ignore bad input rather than resetting the calendar to the epoch
    if (!parsed) {
      return
    }

    cursor.value = parsed
  }

  /**
   * Switches view. Moving to a narrower view keeps the cursor's day, and moving
   * from month view lands on today when today is in the month being viewed.
   * @param nextView - View to switch to
   */
  function setView(nextView: CalendarViewType): void {
    // Nothing to do when the view has not changed
    if (nextView === view.value) {
      return
    }

    // Zooming out of a day keeps that day's month or week on screen
    if (view.value !== 'month' && nextView === 'month') {
      view.value = nextView
      return
    }

    // Zooming into today's month should land on today, not the 1st
    if (view.value === 'month' && cursor.value.isSame(now.value, 'month')) {
      cursor.value = now.value.startOf('day')
    }

    view.value = nextView
  }

  return {
    view,
    cursor,
    title,
    visibleRange,
    days,
    weeks,
    weekdayNames,
    timeSlots,
    gridStartMinute,
    gridEndMinute,
    isTodayVisible,
    next,
    prev,
    goToToday,
    goToDate,
    setView
  }
}
